import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

export function AdminVerifications() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const load = (s = status) => api.get(`/admin/verifications?status=${s}`).then(({ data }) => setItems(data.verifications));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const decide = async (id, decision) => { try { await api.post(`/admin/verifications/${id}`, { decision }); toast.success(`Marked ${decision}`); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <FullSpinner />;
  return (
    <div className="space-y-5" data-testid="admin-verifications">
      <h1 className="font-serif text-3xl font-bold">Verification Queue</h1>
      <div className="flex gap-2">{["pending", "approved", "rejected"].map((s) => <button key={s} onClick={() => setStatus(s)} className={`text-sm px-4 py-2 rounded-full capitalize ${status === s ? "bg-[#C59B27] text-[#3B0A12]" : "bg-[#2A0F14] text-[#D8B4B9]"}`}>{s}</button>)}</div>
      {items.length === 0 ? <p className="text-[#D8B4B9] text-sm">No {status} requests.</p> : items.map((v) => (
        <div key={v.id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 flex items-center justify-between">
          <div><div className="font-medium">{v.profile?.full_name} <span className="font-mono text-xs text-[#8A6D71]">{v.profile?.profile_code}</span></div><div className="text-xs text-[#D8B4B9] capitalize">{v.type} · {new Date(v.created_at).toLocaleDateString()}</div><div className="text-xs text-[#8A6D71] mt-1">Doc: {v.document_ref || "—"} (restricted access)</div></div>
          {status === "pending" && (
            <div className="flex gap-2">
              <button onClick={() => decide(v.id, "approved")} className="btn-gold text-xs px-3 py-1.5 rounded-lg">Approve</button>
              <button onClick={() => decide(v.id, "rejected")} className="text-xs px-3 py-1.5 rounded-lg border border-rose-500/50 text-rose-300">Reject</button>
              <button onClick={() => decide(v.id, "resubmission")} className="text-xs px-3 py-1.5 rounded-lg border border-amber-500/50 text-amber-300">Resubmit</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function AdminReports() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/reports").then(({ data }) => setItems(data.reports));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { try { await api.post(`/admin/reports/${id}`, { status }); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!items) return <FullSpinner />;
  const SCLS = { open: "bg-amber-900/40 text-amber-300", under_review: "bg-sky-900/40 text-sky-300", action_taken: "bg-purple-900/40 text-purple-300", resolved: "bg-emerald-900/40 text-emerald-300" };
  return (
    <div className="space-y-5" data-testid="admin-reports">
      <h1 className="font-serif text-3xl font-bold">Safety & Reports</h1>
      {items.length === 0 ? <p className="text-[#D8B4B9] text-sm">No reports.</p> : items.map((r) => (
        <div key={r.id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div><div className="font-medium">Report: {r.reason}</div><div className="text-xs text-[#D8B4B9]">Against {r.target_profile?.full_name} <span className="font-mono">{r.target_profile?.profile_code}</span></div>{r.details && <div className="text-xs text-[#8A6D71] mt-1">{r.details}</div>}</div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${SCLS[r.status]}`}>{r.status.replace("_", " ")}</span>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => setStatus(r.id, "under_review")} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Under Review</button>
            <button onClick={() => setStatus(r.id, "action_taken")} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Action Taken</button>
            <button onClick={() => setStatus(r.id, "resolved")} className="btn-gold text-xs px-3 py-1.5 rounded-lg">Resolve</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminSubscriptions() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/subscriptions").then(({ data }) => setData(data)); }, []);
  if (!data) return <FullSpinner />;
  return (
    <div className="space-y-5" data-testid="admin-subscriptions">
      <h1 className="font-serif text-3xl font-bold">Finance</h1>
      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-5"><div className="text-sm text-[#D8B4B9]">Total Revenue</div><div className="font-serif text-3xl font-bold text-[#E2C382]">₹{data.revenue}</div></div>
      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm"><thead><tr className="text-left text-xs text-[#C59B27] uppercase border-b border-[#781826]/40"><th className="p-3">Plan</th><th>Amount</th><th>Status</th><th>Transaction</th><th>Date</th></tr></thead>
          <tbody>{data.payments.map((p) => (
            <tr key={p.id} className="border-b border-[#781826]/20"><td className="p-3 capitalize">{p.plan}</td><td>₹{p.amount}</td><td className="capitalize">{p.status}</td><td className="font-mono text-xs">{p.transaction_id || "—"}</td><td>{new Date(p.created_at).toLocaleDateString()}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminSupport() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/support").then(({ data }) => setItems(data.tickets));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.post(`/admin/support/${id}`, { status }); load(); };
  if (!items) return <FullSpinner />;
  return (
    <div className="space-y-5" data-testid="admin-support">
      <h1 className="font-serif text-3xl font-bold">Support Tickets</h1>
      {items.length === 0 ? <p className="text-[#D8B4B9] text-sm">No tickets.</p> : items.map((t) => (
        <div key={t.id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 flex items-center justify-between">
          <div><div className="font-medium">{t.subject}</div><div className="text-xs text-[#D8B4B9] capitalize">{t.category} · {new Date(t.created_at).toLocaleDateString()}</div><div className="text-xs text-[#8A6D71] mt-1">{t.message}</div></div>
          <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} className="bg-[#1A090D] border border-[#781826]/40 rounded-lg text-xs px-2 py-1.5 text-[#FAF5F0]">
            <option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
          </select>
        </div>
      ))}
    </div>
  );
}


export function AdminEvents() {
  const [events, setEvents] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", date: "", time: "", duration: "60 min", limit: 50 });
  const load = () => api.get("/admin/events").then(({ data }) => setEvents(data.events));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name || !form.date) return toast.error("Name and date required");
    try { await api.post("/admin/events", form); toast.success("Event published"); setForm({ name: "", description: "", date: "", time: "", duration: "60 min", limit: 50 }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/events/${id}`); load(); };
  if (!events) return <FullSpinner />;
  return (
    <div className="space-y-5" data-testid="admin-events">
      <h1 className="font-serif text-3xl font-bold">Match Events</h1>
      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 grid sm:grid-cols-2 gap-2">
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" placeholder="Event name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" placeholder="Date (e.g. 2026-07-15)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" placeholder="Time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" type="number" placeholder="Participant limit" value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} />
        <textarea className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white sm:col-span-2" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <button onClick={create} className="btn-gold rounded-lg py-2 sm:col-span-2">Publish Event</button>
      </div>
      {events.map((e) => (
        <div key={e.id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 flex items-center justify-between">
          <div><div className="font-medium">{e.name} <span className="text-xs text-[#8A6D71]">({e.status})</span></div><div className="text-xs text-[#D8B4B9]">{e.date} {e.time} · {e.participant_count}/{e.limit} registered</div></div>
          {e.status !== "archived" && <button onClick={() => del(e.id)} className="text-xs text-rose-300">Archive</button>}
        </div>
      ))}
    </div>
  );
}

export function AdminMatchAssist() {
  const [reqs, setReqs] = useState(null);
  const load = () => api.get("/admin/match-assist").then(({ data }) => setReqs(data.requests));
  useEffect(() => { load(); }, []);
  const action = async (uid, action, extra = {}) => { try { await api.post(`/admin/match-assist/${uid}`, { action, ...extra }); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!reqs) return <FullSpinner />;
  return (
    <div className="space-y-5" data-testid="admin-match-assist">
      <h1 className="font-serif text-3xl font-bold">Match Assist · Relationship Manager</h1>
      {reqs.length === 0 ? <p className="text-[#D8B4B9] text-sm">No assistance requests.</p> : reqs.map((r) => (
        <div key={r.user_id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div><div className="font-medium">{r.profile?.full_name} <span className="font-mono text-xs text-[#8A6D71]">{r.profile?.profile_code}</span></div><div className="text-xs text-[#D8B4B9]">{r.profile?.city} · Status: {r.status}</div></div>
            <select onChange={(e) => action(r.user_id, "status", { status: e.target.value })} value={r.status} className="bg-[#1A090D] border border-[#781826]/40 rounded-lg text-xs px-2 py-1.5 text-white">
              <option value="requested">Requested</option><option value="in_progress">In Progress</option><option value="suggestions_ready">Suggestions Ready</option><option value="completed">Completed</option>
            </select>
          </div>
          {r.note && <p className="text-xs text-[#D8B4B9] mt-2 bg-[#1A090D] rounded-lg p-2">"{r.note}"</p>}
          <div className="flex gap-2 mt-3">
            <button onClick={() => { const ids = prompt("Enter user IDs to suggest (comma separated):"); if (ids) action(r.user_id, "suggest", { user_ids: ids.split(",").map((s) => s.trim()) }); }} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Add Suggestions</button>
            <button onClick={() => { const t = prompt("Internal note (private):"); if (t) action(r.user_id, "note", { text: t }); }} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A090D] text-[#D8B4B9]">Add Note</button>
            <button onClick={() => { const t = prompt("Message to member:"); if (t) action(r.user_id, "message", { text: t }); }} className="btn-gold text-xs px-3 py-1.5 rounded-lg">Message</button>
          </div>
          {(r.internal_notes || []).length > 0 && <div className="mt-2 text-[11px] text-[#8A6D71]">Internal notes: {r.internal_notes.length} (staff only)</div>}
        </div>
      ))}
    </div>
  );
}

export function AdminCoupons() {
  const [coupons, setCoupons] = useState(null);
  const [form, setForm] = useState({ code: "", discount_pct: 20, plan: "any", usage_limit: 100, per_user_limit: 1 });
  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data.coupons));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.code) return toast.error("Code required");
    try { await api.post("/admin/coupons", form); toast.success("Coupon created"); setForm({ ...form, code: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (code) => { await api.delete(`/admin/coupons/${code}`); load(); };
  if (!coupons) return <FullSpinner />;
  return (
    <div className="space-y-5" data-testid="admin-coupons">
      <h1 className="font-serif text-3xl font-bold">Coupons & Promotions</h1>
      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 grid sm:grid-cols-3 gap-2">
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white uppercase" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" type="number" placeholder="Discount %" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} />
        <select className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
          <option value="any">Any plan</option><option value="premium">Premium</option><option value="lifetime">Lifetime</option>
        </select>
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" type="number" placeholder="Total usage limit" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} />
        <input className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" type="number" placeholder="Per-user limit" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} />
        <button onClick={create} className="btn-gold rounded-lg py-2">Create Coupon</button>
      </div>
      {coupons.map((c) => (
        <div key={c.id} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 flex items-center justify-between">
          <div><div className="font-mono font-bold text-[#E2C382]">{c.code}</div><div className="text-xs text-[#D8B4B9]">{c.discount_pct}% off · {c.plan} · used {c.used}/{c.usage_limit || "∞"} · {c.active ? "active" : "inactive"}</div></div>
          {c.active && <button onClick={() => del(c.code)} className="text-xs text-rose-300">Deactivate</button>}
        </div>
      ))}
    </div>
  );
}
